import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { useContent } from "@/context/ContentContext";
import RichTextEditor from "@/components/admin/RichTextEditor";
import ImageUpload from "@/components/admin/ImageUpload";

const GROUPS = {
  "Strona główna": ["home_badge", "home_hero_title", "home_hero_subtitle",
    "home_stat_1_value", "home_stat_1_label", "home_stat_2_value", "home_stat_2_label",
    "home_stat_3_value", "home_stat_3_label", "home_stat_4_value", "home_stat_4_label",
    "home_pillar_1_title", "home_pillar_1_desc", "home_pillar_2_title", "home_pillar_2_desc",
    "home_pillar_3_title", "home_pillar_3_desc", "home_pillar_4_title", "home_pillar_4_desc"],
  "Organy": ["organy_intro"],
  "Status prawny": ["status_intro", "status_body"],
  "Szkolenia": ["szkolenia_intro"],
  "Kontakt": ["kontakt_intro"],
};

const SUBPAGE_HEROES = [
  { key: "organy_hero_image", label: "Organy Izby" },
  { key: "status_hero_image", label: "Status prawny" },
  { key: "szkolenia_hero_image", label: "Konferencje i szkolenia" },
  { key: "kontakt_hero_image", label: "Kontakt" },
];

const LABELS = {
  home_badge: "Odznaka (badge)", home_hero_title: "Nagłówek hero", home_hero_subtitle: "Podtytuł hero",
  organy_intro: "Wstęp - Organy", status_intro: "Wstęp - Status", status_body: "Treść statutu",
  szkolenia_intro: "Wstęp - Szkolenia", kontakt_intro: "Wstęp - Kontakt",
};

export default function AdminContent() {
  const { reload } = useContent();
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(null);

  useEffect(() => { api.get("/content").then((r) => setData(r.data)).catch(() => {}); }, []);

  const save = async (key) => {
    setSaving(key);
    try {
      await api.put(`/content/${key}`, { value: data[key] });
      toast.success("Zapisano");
      reload();
    } catch { toast.error("Błąd zapisu"); }
    finally { setSaving(null); }
  };

  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-ztk-navy dark:text-white">Treści podstron</h2>
      <p className="mt-1 text-sm text-muted-foreground">Edytuj każdy tekst wyświetlany na stronie</p>
      <div className="mt-8 space-y-8">
        <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="mb-1 font-display text-lg font-semibold text-ztk-blue">Zdjęcie na stronie głównej</h3>
          <p className="mb-4 text-sm text-muted-foreground">Zdjęcie w sekcji powitalnej (hero). Zalecany format poziomy.</p>
          <ImageUpload
            value={data.home_hero_image || ""}
            multiple={false}
            label="Zdjęcie hero"
            onChange={(v) => setData((d) => ({ ...d, home_hero_image: v }))}
          />
          <Button size="sm" className="mt-4 gap-1.5 bg-ztk-blue" onClick={() => save("home_hero_image")} disabled={saving === "home_hero_image"} data-testid="content-save-home_hero_image">
            {saving === "home_hero_image" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Zapisz zdjęcie
          </Button>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="mb-1 font-display text-lg font-semibold text-ztk-blue">Zdjęcia nagłówkowe podstron</h3>
          <p className="mb-6 text-sm text-muted-foreground">Zdjęcie tła w nagłówku (hero) dla podstron. Zalecany format poziomy.</p>
          <div className="space-y-8">
            {SUBPAGE_HEROES.map((h) => (
              <div key={h.key} className="border-t border-border pt-6 first:border-t-0 first:pt-0">
                <p className="mb-3 font-medium text-ztk-navy dark:text-white">{h.label}</p>
                <ImageUpload
                  value={data[h.key] || ""}
                  multiple={false}
                  label="Zdjęcie nagłówka"
                  onChange={(v) => setData((d) => ({ ...d, [h.key]: v }))}
                />
                <Button size="sm" className="mt-4 gap-1.5 bg-ztk-blue" onClick={() => save(h.key)} disabled={saving === h.key} data-testid={`content-save-${h.key}`}>
                  {saving === h.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Zapisz zdjęcie
                </Button>
              </div>
            ))}
          </div>
        </div>

        {Object.entries(GROUPS).map(([group, keys]) => (
          <div key={group} className="rounded-2xl border border-border bg-card p-6">
            <h3 className="mb-4 font-display text-lg font-semibold text-ztk-blue">{group}</h3>
            <div className="space-y-4">
              {keys.map((key) => {
                const isLong = key === "status_body" || key.includes("subtitle") || key.includes("desc") || key.includes("intro");
                const isRich = key === "status_body";
                return (
                  <div key={key} className="flex flex-col gap-2 sm:flex-row sm:items-start">
                    <label className="w-56 shrink-0 pt-2 text-sm font-medium">{LABELS[key] || key}</label>
                    <div className="flex flex-1 gap-2">
                      {isRich ? (
                        <div className="flex-1">
                          <RichTextEditor value={data[key] || ""} onChange={(v) => setData({ ...data, [key]: v })} testid={`content-${key}`} />
                        </div>
                      ) : isLong ? (
                        <Textarea rows={2} value={data[key] || ""} onChange={(e) => setData({ ...data, [key]: e.target.value })} data-testid={`content-${key}`} />
                      ) : (
                        <Input value={data[key] || ""} onChange={(e) => setData({ ...data, [key]: e.target.value })} data-testid={`content-${key}`} />
                      )}
                      <Button size="icon" variant="outline" onClick={() => save(key)} disabled={saving === key} data-testid={`content-save-${key}`}>
                        {saving === key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
